import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useConfigureFooter } from '~hooks/useHelpers'
import VolumeInNegotiation from '../components/Negotiations/VolumeInNegotiation'
import NegotiationsAccordion from '../components/Negotiations/NegotiationsAccordion'
import NegotiationUpdateModal from '../components/Negotiations/NegotiationUpdateModal'
import FunnelMassDateButton from '../components/shared/FunnelMassDateButton'
import FunnelLayout from '../components/shared/FunnelLayout'
import useFunnelRowEdits from '../hooks/useFunnelRowEdits'
import useFunnelUnsavedChanges from '../hooks/useFunnelUnsavedChanges'
import { selectedIdsFromMap, toggleSelection, setSelectionForIds } from '../helpers/selection'
import { isPastMonth } from '../helpers/pastDate'
import useFunilObraUnidadeMedida from '~fetchs/Funnel/useFunilObraUnidadeMedida'
import useFunilObraGrupoProduto from '~fetchs/useFunilObraGrupoProduto'
import useFunilStatus from '~fetchs/useFunilStatus'
import { db } from '~db/db'
import { fixObject, formatDateByPattern, getDevice, getMeasure, resetFunnel } from '../helpers/funnelHelpers'
import { formatMonth } from '../helpers/utils'
import { randomId } from '~pages/Funnel/utils'
import useRequisicoesOffline from '~fetchs/useRequisicoesOffline'
import { IconSuccess, WarningIcon2 } from '~misc/icons'
import MessageForm from '~components/Form/components/MessageForm'
import LoaderFull from '~components/Form/components/LoaderFull'
import Modal from '~components/Modal';
import { IconAlert } from "~misc/icons";
import { useSearchParams } from 'react-router-dom';
import { getLocation } from "~services/utils/geoLocation";
import { deviceType, isDesktop, isTablet } from 'react-device-detect'
import { formatDateToCustomString } from '~pages/PriceSearchV2/utils/searchHelpers'
import { getChartMonthRange, isInChartMonthRange } from '../helpers/chart/ChartPeriod';

const FunnelNextNegotiations = () => {
  const [clients, setClients] = useState({});
  const [productGroup, setProductGroup] = useState('')
  const [period, setPeriod] = useState('default');
  const monthRange = useMemo(() => getChartMonthRange(period), [period]);
  const [status, setStatus] = useState(0)
  const [initialStatus, setInitialStatus] = useState(0);
  const [accordionData, setAccordionData] = useState([])
  const [selected, setSelected] = useState([]);
  const [dirty, setDirty] = useState({});
  const [showOldDateModal, setShowOldDateModal] = useState(false);
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  
  const [showMessageForm, setShowMessageForm] = useState({
    text: "",
    type: "success",
    icon: IconSuccess,
  });

  const [editOpen, setEditOpen] = useState(false);
  const [funnel, setFunnel] = useState([]);

  const { funilObraUnidadeMedidaObjeto } = useFunilObraUnidadeMedida({});
  const { optionFunilObraGruposProduto, funilObraGrupoProdutoObjeto } = useFunilObraGrupoProduto({ showAll: true });
  const { funilStatusObjBySlug, funilStatusObj, optionFunisStatus } = useFunilStatus({});
  const { save } = useRequisicoesOffline();  
  const { clearAll } = useFunnelRowEdits('funil-proximas-negociacoes-edits')
  const search = searchParams.get('search') || '';

  useFunnelUnsavedChanges(false, undefined, clearAll);
  const isDirty = Object.values(dirty).some(Boolean) && !Object.values(dirty).includes('invalid-date');

  const getFunnel = async () => {
    let data = [];
    await db.funilObraDashboard.filter(e => !e.deletadoEm).each(e => data.push(e));

    return data;
  }

  const toggleSelected = useCallback(
    (id) => setSelected((prev) => toggleSelection(prev, id)),
    []
  )

  const handleSelectAll = useCallback((ids, value) => {
    const today = new Date();
    const eligibleIds = accordionData.flatMap(cliente => cliente.produtos)
      .filter(produto => ids.includes(produto.id) &&
        isPastMonth(new Date(`${produto.dataNegociacaoOriginal}T00:00:00`), today))
      .map(produto => produto.id);

    setSelected(prev => setSelectionForIds(prev, eligibleIds, value));
  }, [accordionData]);

  const selectedIds = selectedIdsFromMap(selected)

  const handleConfirm = useCallback(async ({ date } = {}) => {
    if (date && selectedIds?.length) {
      setLoading(true);
      const items = await db.funilObraDashboard.bulkGet(selectedIds.map(n => Number(n)));

      const updatedItems = items
        .filter(Boolean)
        .map(item => ({
          ...item,
          dataNegociacao: date
        }));

      await db.funilObraDashboard.bulkPut(updatedItems);

      const dirtyItens = await getDirty();

      const device = getDevice(isTablet, isDesktop, deviceType);
      let pos = await getLocation();
      for (const e of Object.keys(pos)) 
        pos[e] = pos[e] ? pos[e].toString() : null;

      const data = [
        ...updatedItems.map(e => ({
          id: e.id,
          clienteId: e.clienteId,
          dataNegociacao: e.dataNegociacao,
          funilObraGrupoProdutoId: e.funilObraGrupoProdutoId
        })),
        ...dirtyItens,
      ].map(e => ({
        ...e,
        ...pos,
        dispositivo: device ?? "",
        ultimaAtualizacao: formatDateToCustomString(new Date())
      }));

      const uniqueData = [...new Map(data.map(item => [item.id, item])).values()];
      handleSubmitFunnel(uniqueData);
    }

    setSelected({});
    clearAll();
  }, [selectedIds, clearAll])

  const handleSubmitFunnel = async (confirmedData) => {
    const obj = {
      id: randomId(15),
      type: "funilObraDataNegociacao",
      toSend: confirmedData,
      createdAt: new Date().toISOString(),
      sended: false,
    };

    try {
      const response = await save(obj);

      if (response?.isOffline) {
        setShowMessageForm({
          text: `Funil atualizado com sucesso (modo offline)`,
          icon: IconSuccess,
          type: "success",
        });
        return;
      }

      if(response && Array.isArray(response)) {
        const data = response;
        const ids = data.map(e => Number(e.clienteId));
        await resetFunnel({ data, table: db.funilObra, clienteId: ids });

        const funnelItems = [];
        await db.funilObra.each(item => funnelItems.push(fixObject({ obj: item})));
        
        await resetFunnel({ data: funnelItems });
        setFunnelData();

        setShowMessageForm({
          text: `Atualizações realizadas com sucesso`,
          icon: IconSuccess,
          type: "success",
        });
      }

      setSelected({});
      setDirty({});
      setLoading(false);
    } catch (error) {
      const message = error?.message ?? "";
      setShowMessageForm({
        text: `Ocorreu um erro durante o envio do funil - ${message ?? ""}`,
        icon: WarningIcon2,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  const hasPastDates = useCallback(() => {
    const current = new Date();

    return accordionData.some(cliente =>
      cliente.produtos.some(produto => {
        if (dirty[`id-${produto.id}`] !== true) return false;

        return isPastMonth(new Date(produto.dataNegociacaoOriginal + "T00:00:00"), current);
      })
    );
  }, [accordionData, dirty]);

  const handleSaveConfirm = useCallback(async () => {
    if(!Object.values(dirty)?.length) return;

    if (hasPastDates()) {
      setShowOldDateModal(true);
      return;
    }

    setLoading(true);

    const data = await getDirty();

    if(!data.length){
      setLoading(false);
      return;
    }

    handleSubmitFunnel(data);
  }, [clearAll, dirty, hasPastDates, isDirty]);

  const getDirty = async () => {
    const ids = Object.entries(dirty).filter(([_, v]) => v === true).map(([k]) => Number(k.replace("id-",'') ?? 0));
    const data = [];
    await db.funilObraDashboard.where('id').anyOf(ids).each(item => data.push({
      id: item.id,
      dataNegociacao: item.dataNegociacao,
      clienteId: item.clienteId,
      funilObraPaiId: item.funilObraPaiId ?? item.id ?? null,
      funilObraGrupoProdutoId: item.funilObraGrupoProdutoId
    }));

    return data;
  }

  useConfigureFooter(
    {
      buttonsComponent: (selectedIds.length > 0 || isDirty) ? (
        <div className="flex gap-2">
          {isDirty && (
            <button
              type="button"
              onClick={() => handleSaveConfirm()}
              className="button"
              style={{ backgroundColor: '#0DB14B', color: '#fff', borderColor: '#0DB14B' }}
            >
              Salvar
            </button>
          )}
          {selectedIds.length > 0 && (
            <FunnelMassDateButton onClick={() => setEditOpen(true)} count={selectedIds.length} label="Atualizar datas" />
          )}
        </div>
      ) : null,
    },
    [selectedIds.length, isDirty]
  )

  const handleFilterChange = ({ productGroup: pg, status: st }) => {
    setProductGroup(pg)
    setStatus(st);
  }

  const emNegociacaoStatusId = funilStatusObjBySlug['em-negociacao']?.id ?? 0;
  
  const filteredAccordion = useMemo(() => {
    const searchValue = search.toLowerCase().trim();


    return (emNegociacaoStatusId ? accordionData : [])
      .map((client) => ({...client, 
        produtos: client.produtos.filter(p => {

          return (
            (!productGroup || productGroup == p.funilObraGrupoProdutoId) &&
            (!status || status == p.funilObraStatusId) &&
            isInChartMonthRange(p.dataNegociacaoOriginal, monthRange)
          );
        }),
      }))
      .filter((client) => {
        if (!client.produtos.length) return false;
        if (!searchValue) return true;

        return (
          client.nome?.toLowerCase().includes(searchValue) ||
          client.nomeObra?.toLowerCase().includes(searchValue) ||
          (client.isProspect ? client.codigoProspect : client.codigo)?.toString().toLowerCase().includes(searchValue) ||
          client.grupoCentralizadorSearch?.includes(searchValue)
        );
      });
  }, [accordionData, productGroup, status, funilStatusObjBySlug, search, monthRange ]);

  const getClients = async (items) => {
    const clienteIds = [...new Set(items.map((item) => item.clienteId))];

    const clientes = {};

    await db.cliente.where('id').anyOf(clienteIds).each(e => {
      clientes[`id-${e.id}`] = {
        codigo: e.codigo,
        codigoProspect: e.codigoProspect,
        nome: e.razaoSocial,
        nomeObra: e.nomeObra,
        grupoCentralizadorSearch: String(e.grupoCentralizador ?? '').toLowerCase(),
        isProspect: e.clienteProspect == 1,
      }
    });

    setClients(clientes);
  }

  const setFunnelData = async () => {
    const items = await getFunnel();
    setFunnel(items);
  }

  const conversionEnabled = !status;
  const conversionByMonth = useMemo(() => {
    if (!conversionEnabled) return {};
    const convTemp = {};
    accordionData.forEach(cliente => {
      cliente.produtos.forEach(cur => {
        const date = new Date(`${cur.dataNegociacaoOriginal}T00:00:00`);
        const month = date.getMonth() + 1;
        const formattedDate = formatDateByPattern(date, "mm-yyyy");

        const statusObj = funilStatusObj[`id-${cur.funilObraStatusId}`];

        convTemp[formattedDate] ??= {
          ganha: 0,
          total: 0,
        };

        convTemp[formattedDate].total += cur.volume;

        if (statusObj?.slug === "ganha") {
          convTemp[formattedDate].ganha += cur.volume;
        }
      });
    });

    return convTemp;
  }, [accordionData, funilStatusObj, conversionEnabled]);

  const { graphData, conversionData } = useMemo(() => {
    const groupByStatus = !status;
    const temp = {};

    filteredAccordion.forEach(a => {
      (a.produtos || []).forEach(cur => {
        const date = new Date(`${cur.dataNegociacaoOriginal}T00:00:00`);
        const month = date.getMonth() + 1;
        const formattedDate = formatDateByPattern(date, "mm-yyyy");

        if (groupByStatus) {
          const statusId = cur.funilObraStatusId ?? 0;
          const statusObj = funilStatusObj[`id-${statusId}`];
          if (!statusObj) return;

          temp[formattedDate] ??= { monthNumber: month, label: `${formatMonth(month).substring(0, 3)}/${formatDateByPattern(date, "yyyy")}`, date: cur.dataNegociacaoOriginal, groups: {} };
          temp[formattedDate].groups[`status-${statusId}`] ??= { volume: 0, slug: statusObj.slug, label: statusObj.nome };
          temp[formattedDate].groups[`status-${statusId}`].volume += cur.volume;
        } else {
          const groupId = cur.funilObraGrupoProdutoId ?? 0;
          const group = funilObraGrupoProdutoObjeto[`id-${groupId}`];
          const measure = funilObraUnidadeMedidaObjeto[`id-${cur.funilObraUnidadeMedidaId}`];

          if (group && measure) {
            const formattedMeasure = getMeasure(measure.nome ?? "") ?? "";
            temp[formattedDate] ??= { monthNumber: month, label: `${formatMonth(month).substring(0, 3)}/${formatDateByPattern(date, "yyyy")}`, date: cur.dataNegociacaoOriginal, groups: {} };
            const key = `id-${groupId}`;
            temp[formattedDate].groups[key] ??= {
              volume: 0,
              slug: key,
              groupId: groupId,
              label: productGroup
                    ? (funilObraGrupoProdutoObjeto[`id-${groupId}`]?.nome ?? 'Grupo')
                    : `${group.nome} (${formattedMeasure})`,
            };
            temp[formattedDate].groups[key].volume += cur.volume;
          }
        }
      });
    });

    const sortedKeys = Object.keys(temp).sort((a, b) => temp[a].date.localeCompare(temp[b].date));
    const graphDataResult = sortedKeys.map(k => temp[k]);

    const conversionDataResult = groupByStatus
      ? sortedKeys.map(k => {
          const c = conversionByMonth[k] ?? { ganha: 0, total: 0 };
          return c.total > 0 ? Math.round((c.ganha / c.total) * 1000) / 10 : 0;
        })
      : [];

    return { graphData: graphDataResult, conversionData: conversionDataResult };
  }, [filteredAccordion, conversionByMonth, productGroup, status, funilObraGrupoProdutoObjeto, funilObraUnidadeMedidaObjeto, funilStatusObj]);

  const updateData = useCallback(({ clienteId, id, data }) => {
    db.funilObraDashboard.update(id, {
      ...data
    });

    setAccordionData(prev => {
      return prev.map(t => {
        if (t.clienteId !== clienteId) return t;

        return {
          ...t,
          produtos: t.produtos.map(p => {
            if (p.id !== id) return p;

            return {
              ...p,
              dataNegociacao: data.dataNegociacao.split('-')?.reverse()?.join('/'),
              dataNegociacaoOriginal: data.dataNegociacao
            };
          })
        };
      });
    });
  }, []);

  useEffect(() => {
    if(
      Object.entries(funilStatusObjBySlug ?? {})?.length &&
      Object.entries(clients ?? {})?.length &&
      Object.entries(funilObraGrupoProdutoObjeto ?? {})?.length &&
      Object.entries(funilObraUnidadeMedidaObjeto ?? {})?.length &&
      funnel?.length
    ) {
        const formattedFunnel = funnel.reduce((acc, cur) => {
          const key = cur.funilObraGrupoProdutoId ?? 0;
          const client = clients[`id-${cur.clienteId}`] ?? null;

          const group =
            funilObraGrupoProdutoObjeto[`id-${key}`];

          const measure =
            funilObraUnidadeMedidaObjeto[`id-${cur.funilObraUnidadeMedidaId}`];

          const date = new Date(`${cur.dataNegociacao}T00:00:00`);

          if (group && measure) {
            acc[cur.clienteId] ??= {
              clienteId: cur.clienteId,
              codigo: client?.codigo,
              codigoProspect: client?.codigoProspect,
              nome: client?.nome,
              nomeObra: client?.nomeObra,
              grupoCentralizadorSearch: client?.grupoCentralizadorSearch,
              isProspect: client?.isProspect ?? false,
              produtos: [],
            }

            acc[cur.clienteId].produtos.push({
              id: cur.id,
              funilObraGrupoProdutoId: cur.funilObraGrupoProdutoId,
              funilObraUnidadeMedidaId: cur.funilObraUnidadeMedidaId,
              funilObraStatusId: cur.funilObraStatusId,
              grupoProduto: group.nome,
              volume: cur.volumeNegociado,
              dataNegociacao: formatDateByPattern(date, "dd/mm/YYYY"),
              dataNegociacaoOriginal: cur.dataNegociacao,
              hasWarning: false,
              status: funilStatusObj[`id-${cur.funilObraStatusId}`]?.nome ?? 0,
              checked: false
            })
          }

          return acc;
        }, {});

        setAccordionData(Object.values(formattedFunnel));
      }
  }, [funilObraGrupoProdutoObjeto, funilObraUnidadeMedidaObjeto, funilStatusObj, funnel, clients])

  useEffect(() => {
    const id = funilStatusObjBySlug['em-negociacao']?.id ?? 0;
    if (id && !initialStatus) {
      setInitialStatus(id);
      setStatus(id);
    }
  }, [funilStatusObjBySlug, initialStatus]);


  useEffect(() => {
    (async() => {        
        let data = [];        
        await db.funilObra.filter(e => !e.deletadoEm).each(e => data.push(fixObject({ obj: e })));
        await resetFunnel({ data });

        await getClients(data);
        setFunnel(data);
    })()
  }, []);

  return (
    <FunnelLayout>
      {
        loading ? <LoaderFull /> : null
      }
      <VolumeInNegotiation
        period={period}
        onPeriodChange={setPeriod}
        data={graphData}
        conversionData={conversionData}
        productGroupOptions={optionFunilObraGruposProduto}
        statusOptions={optionFunisStatus}
        initialStatus={initialStatus}
        onFilterChange={handleFilterChange}
      />
      <NegotiationsAccordion
        items={filteredAccordion}
        selected={selected}
        onToggle={toggleSelected}
        onSelectAll={handleSelectAll}
        setDirty={setDirty}
        updateData={updateData}
        emNegociacaoStatusId={emNegociacaoStatusId}
      />
      <NegotiationUpdateModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onConfirm={handleConfirm}
        targetIds={selectedIds}
      />
      {showMessageForm.text ? (
        <MessageForm
          text={showMessageForm.text}
          type={showMessageForm.type}
          showMessage={showMessageForm}
          setShowMessage={() =>
            setShowMessageForm({
              text: "",
            })
          }
          icon={showMessageForm.icon}
        />
      ) : null}

      <Modal
        isOpen={showOldDateModal}
        onClose={() => setShowOldDateModal(false)}
        title={
          <div >
            <IconAlert />
            <p className='my-4'>Existem items no passado</p>
          </div>
        }
        sub={
          <div>
            <p>
              Há negociações com datas anteriores à data atual.
            </p> 
            <p>
              Ajuste essas datas antes de salvar.
            </p>
          </div>
        }
        status="alert"
        hideButtonOne={true}
        hideButtonTwo={true}
      />
    </FunnelLayout>
  )
}

export default FunnelNextNegotiations
